import React, { useRef, useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { RootState } from 'store';

import useStoreTranslation from 'hooks/useStoreTranslation';

import { RegistrationOnboardingLayoutProps } from '..';

import {
  Container,
  Left,
  Right,
  Logo,
  Content,
  RightContent,
  RightAsset,
  RollingContainer,
  RollingItems,
  RollingStub,
  RollingItem,
  RollingGradient,
} from './styles';

const RegistrationOnboardingDesktopLayout = ({ children, sideAssetUrl }: RegistrationOnboardingLayoutProps) => {
  const history = useHistory();
  const { t } = useStoreTranslation();

  const rollingItemsRef = useRef<HTMLDivElement>(null);
  const virtualFirstRef = useRef<HTMLDivElement>(null);
  const virtualLastRef = useRef<HTMLDivElement>(null);

  const [activeRollingItem, setActiveRollingItem] = useState<number>(0);
  const [lastAssetUrl, setLastAssetUrl] = useState<string | undefined>();

  const ROLLING_MESSAGES = [
    t('registrationOnboarding.rollingMessages.manageCosts'),
    t('registrationOnboarding.rollingMessages.searchFlights'),
    t('registrationOnboarding.rollingMessages.addExpenses'),
    t('registrationOnboarding.rollingMessages.establishRules'),
    t('registrationOnboarding.rollingMessages.trackRequests'),
  ];
  const ROLLING_LAST_INDEX = ROLLING_MESSAGES.length - 1;

  const handleRollWrapping = useCallback(
    (index: number) => {
      let item: HTMLDivElement | null;
      if (index === -1) item = virtualLastRef.current;
      else item = virtualFirstRef.current;

      requestAnimationFrame(() => item?.scrollIntoView({ behavior: 'smooth', block: 'center' }));

      // Wrapping last -> first
      if (index === ROLLING_LAST_INDEX + 1) {
        setActiveRollingItem(0);
        setTimeout(() => {
          const first = rollingItemsRef.current?.children[0];
          if (!first) return;
          first.scrollIntoView({ block: 'center' });
        }, 250);
      }

      // Wrapping last <- first
      if (index === -1) {
        setActiveRollingItem(ROLLING_LAST_INDEX);
        setTimeout(() => {
          const last = rollingItemsRef.current?.children[ROLLING_LAST_INDEX];
          if (!last) return;
          last.scrollIntoView({ block: 'center' });
        }, 250);
      }
    },
    [ROLLING_LAST_INDEX],
  );

  const goToRollingIndex = useCallback(
    (index: number) => {
      setActiveRollingItem(index);

      const item = rollingItemsRef.current?.children[index];
      if (!item) {
        handleRollWrapping(index);
        return;
      }
      requestAnimationFrame(() => item.scrollIntoView({ behavior: 'smooth', block: 'center' }));
    },
    [handleRollWrapping],
  );

  const handleResize = () => goToRollingIndex(activeRollingItem);

  useEffect(() => goToRollingIndex(0), [goToRollingIndex]);

  useEffect(
    () =>
      history.listen((location, action) => {
        if (action === 'PUSH') goToRollingIndex(activeRollingItem + 1);
        else goToRollingIndex(activeRollingItem - 1);
      }),
    [history, activeRollingItem, goToRollingIndex],
  );

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  });

  // Necessary to let the image fade away
  useEffect(() => {
    if (sideAssetUrl) setLastAssetUrl(sideAssetUrl);
  }, [sideAssetUrl]);

  const companyConfiguration = useSelector((state: RootState) => state.companyConfiguration.configuration);
  const companyConfigurationLogo = useMemo(
    () => companyConfiguration?.ownerImageLogoBytes || companyConfiguration?.ownerImageLogo || '/corporate/images/logo-netviajes.svg',
    [companyConfiguration],
  );

  return (
    <Container>
      <Left padding={(window.location.href.indexOf('customerSupport') !== -1)}>
        {(window.location.href.indexOf('customerSupport') !== -1 || window.location.href.indexOf('login') !== -1) && (
          <Logo src={companyConfigurationLogo} />
        )}
        <Content>{children}</Content>
      </Left>
      <Right>
        <RightContent>
          <RightAsset visible={!!sideAssetUrl} imageUrl={lastAssetUrl} />
          <RollingContainer visible={!sideAssetUrl}>
            <RollingGradient />

            <RollingStub />
            <RollingItem active={false}>{ROLLING_MESSAGES[ROLLING_LAST_INDEX - 1]}</RollingItem>
            <RollingItem ref={virtualLastRef} active={activeRollingItem === ROLLING_LAST_INDEX}>
              {ROLLING_MESSAGES[ROLLING_LAST_INDEX]}
            </RollingItem>

            <RollingItems ref={rollingItemsRef}>
              {ROLLING_MESSAGES.map((message, index) => (
                <RollingItem key={message} active={activeRollingItem === index}>
                  {message}
                </RollingItem>
              ))}
            </RollingItems>

            <RollingItem ref={virtualFirstRef} active={activeRollingItem === 0}>
              {ROLLING_MESSAGES[0]}
            </RollingItem>
            <RollingItem active={false}>{ROLLING_MESSAGES[1]}</RollingItem>
            <RollingStub />
          </RollingContainer>
        </RightContent>
      </Right>
    </Container>
  );
};

export default RegistrationOnboardingDesktopLayout;
